if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.variants = this.dataset.variants ? JSON.parse(this.dataset.variants) : [];

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initAccordion();
        this.initShadeFinder();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }
      initAccordion() {
        const accordions = this.querySelectorAll('.product__accordion > details');
        accordions.forEach((accordion) => {
          if (accordion.dataset.accordionInitialized) return;
          accordion.dataset.accordionInitialized = 'true';

          accordion.addEventListener('toggle', (event) => {
            if (!event.target.open && event.target.classList.contains('is-locked-open')) {
              event.target.setAttribute('open', '');
              return;
            }
            if (event.target.open) {
              accordions.forEach((otherAccordion) => {
                if (
                  otherAccordion !== event.target &&
                  otherAccordion.hasAttribute('open') &&
                  !otherAccordion.classList.contains('is-locked-open')
                ) {
                  otherAccordion.removeAttribute('open');
                }
              });
            }
          });
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          this.initAccordion();
          this.initShadeFinder();
        });
      }

      initShadeFinder() {
        const triggers = Array.from(this.querySelectorAll('[data-shade-finder-open]'));
        const overlay = this.querySelector('[data-shade-finder-overlay]');
        const closeBtn = overlay ? overlay.querySelector('[data-shade-finder-close]') : null;
        const configScripts = Array.from(this.querySelectorAll('[data-shade-finder-config]'));
        if (!triggers.length || !overlay || !configScripts.length) return;
        const existingGlobalOverlay = document.body.querySelector('[data-shade-finder-overlay][data-sf-global="true"]');
        if (existingGlobalOverlay && existingGlobalOverlay !== overlay) existingGlobalOverlay.remove();
        if (!overlay.dataset.sfGlobalized) {
          overlay.dataset.sfGlobalized = 'true';
          overlay.dataset.sfGlobal = 'true';
          document.body.appendChild(overlay);
        }
        if (overlay.dataset.sfInitialized) return;
        overlay.dataset.sfInitialized = 'true';

        const configs = new Map();
        configScripts.forEach((scriptEl) => {
          const key = scriptEl.getAttribute('data-shade-finder-config') || 'default';
          try {
            const parsed = JSON.parse(scriptEl.textContent || '{}');
            if (parsed && Array.isArray(parsed.variants) && Array.isArray(parsed.optionNames)) {
              configs.set(key, parsed);
            }
          } catch (e) {}
        });
        if (!configs.size) return;

        const steps = Array.from(overlay.querySelectorAll('[data-sf-step]'));
        const resultWrap = overlay.querySelector('[data-sf-result]');
        const matchBadge = overlay.querySelector('.match-badge');
        const resultKicker = overlay.querySelector('.sf-result-kicker');
        const resultName = overlay.querySelector('[data-sf-result-name]');
        const recommendationsEl = overlay.querySelector('[data-sf-recommendations]');
        const errorEl = overlay.querySelector('[data-sf-error]');
        const selectBtn = overlay.querySelector('[data-sf-select]');
        const retryBtn = overlay.querySelector('[data-sf-retry]');
        const progressContainer = overlay.querySelector('[data-sf-progress-container]');
        const progressBar = overlay.querySelector('[data-sf-progress-bar]');
        const totalSteps = Math.max(steps.length, 1);
        const progressStates = totalSteps + 1;

        const state = { tone: null, undertone: null };
        let matchingVariants = [];
        let selectedItemKeys = new Set();
        let activeConfig = null;
        let isAddingShadeFinderItems = false;

        const toneLabels = {
          'very-light': 'Sehr Hell',
          light: 'Hell',
          medium: 'Mittel',
          deep: 'Dunkel / Tief',
        };

        const undertoneLabels = {
          cool: 'Kuehl',
          neutral: 'Neutral',
          warm: 'Warm',
        };

        const getSelectionName = () => {
          const toneLabel = toneLabels[state.tone] || '';
          const undertoneLabel = undertoneLabels[state.undertone] || '';
          return {
            toneLabel,
            undertoneLabel,
            selectedShadeName: [toneLabel, undertoneLabel].filter(Boolean).join(' - '),
          };
        };

        overlay.hidden = true;
        document.documentElement.classList.remove('sf-open');

        const setOpen = (open) => {
          overlay.hidden = !open;
          document.documentElement.classList.toggle('sf-open', open);
        };

        const showStep = (stepNum) => {
          steps.forEach((el) => {
            const n = Number(el.getAttribute('data-sf-step'));
            el.hidden = n !== stepNum;
          });
          if (resultWrap) resultWrap.hidden = true;
          if (errorEl) errorEl.hidden = true;
          if (progressContainer) progressContainer.hidden = false;
          if (progressBar) {
            const progress = Math.max(0, Math.min(100, (stepNum / progressStates) * 100));
            progressBar.style.width = `${progress}%`;
          }
        };

        const escapeHtml = (value) =>
          String(value ?? '').replace(/[&<>"']/g, (char) => {
            const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
            return entities[char] || char;
          });

        const getCart = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const getAddUrl = () => {
          if (typeof routes !== 'undefined' && routes.cart_add_url) return routes.cart_add_url;
          if (window.Shopify?.routes?.root) return `${window.Shopify.routes.root}cart/add.js`;
          return '/cart/add.js';
        };
        const getSelectionKey = (role, id) => `${role}:${id}`;

        const getRecommendedVariants = () => {
          const seen = new Set();
          return matchingVariants.reduce((items, variant) => {
            const recommended = variant?.recommended;
            const recommendedId = String(recommended?.id || '').trim();
            if (!recommendedId || seen.has(recommendedId)) return items;
            seen.add(recommendedId);
            items.push(recommended);
            return items;
          }, []);
        };

        const getSelectableItems = () => {
          const items = [];

          matchingVariants.forEach((variant) => {
            if (!variant?.id) return;
            items.push({
              key: getSelectionKey('match', variant.id),
              id: variant.id,
              role: 'match',
            });
          });

          getRecommendedVariants().forEach((variant) => {
            if (!variant?.id) return;
            items.push({
              key: getSelectionKey('recommended', variant.id),
              id: variant.id,
              role: 'recommended',
            });
          });

          return items;
        };

        const getSelectedCartItems = () => {
          const itemsById = new Map();

          getSelectableItems().forEach((item) => {
            if (!selectedItemKeys.has(item.key)) return;

            const itemId = String(item.id);
            if (itemsById.has(itemId)) {
              itemsById.get(itemId).quantity += 1;
            } else {
              itemsById.set(itemId, { id: item.id, quantity: 1 });
            }
          });

          return Array.from(itemsById.values());
        };

        const setSelectButtonLoading = (loading) => {
          if (!selectBtn) return;
          const spinner = selectBtn.querySelector('.loading__spinner');
          selectBtn.classList.toggle('loading', loading);
          if (loading) selectBtn.setAttribute('aria-disabled', 'true');
          if (spinner) spinner.classList.toggle('hidden', !loading);
        };

        const updateSelectButtonState = () => {
          if (!selectBtn) return;
          const disabled = isAddingShadeFinderItems || getSelectedCartItems().length === 0;
          selectBtn.disabled = disabled;
          if (disabled) selectBtn.setAttribute('aria-disabled', 'true');
          else selectBtn.removeAttribute('aria-disabled');
        };

        const buildCardMarkup = ({
          title,
          subtitle,
          image,
          tag,
          hero = false,
          role,
          selected = true,
          id = '',
        }) => {
          const imageMarkup = image
            ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy">`
            : '<div class="prod-img-placeholder">QUDE</div>';
          const bodyMarkup = `
            <div class="sf-product-copy">
              <strong>${escapeHtml(title)}</strong>
              <span>${escapeHtml(subtitle)}</span>
            </div>
          `;

          return `
            <div class="product-recommendation${hero ? ' hero-product' : ''}${selected ? ' is-selected' : ''}" data-sf-card-role="${escapeHtml(role || '')}" data-sf-card-id="${escapeHtml(id)}" data-selected="${selected ? 'true' : 'false'}">
              ${tag ? `<div class="product-tag">${escapeHtml(tag)}</div>` : ''}
              <div class="sf-product-image">${imageMarkup}</div>
              ${bodyMarkup}
              <div class="checkbox-container">
                <input type="checkbox" data-sf-card-toggle="${escapeHtml(role || '')}" aria-label="Toggle ${escapeHtml(title)}"${selected ? ' checked' : ''}>
              </div>
            </div>
          `;
        };

        const renderRecommendations = () => {
          if (!recommendationsEl) return;

          const cards = [];
          const hasSingleMatch = matchingVariants.length === 1;
          const recommendedVariants = getRecommendedVariants();

          cards.push('<p class="sf-section-heading">Dein Match:</p>');

          matchingVariants.forEach((variant) => {
            const matchTitle = variant?.productTitle || activeConfig?.productTitle || 'QUDE';
            const matchSubtitle = variant?.title ? `Nuance: ${variant.title}` : '';

            cards.push(
              buildCardMarkup({
                title: matchTitle,
                subtitle: matchSubtitle,
                image: variant?.image || variant?.swatchImage || '',
                tag: 'Gesuchtes Produkt',
                hero: hasSingleMatch,
                role: 'match',
                id: variant?.id,
                selected: selectedItemKeys.has(getSelectionKey('match', variant?.id)),
              })
            );
          });

          if (recommendedVariants.length) {
            cards.push('<p class="sf-section-heading">Ergaenze dein perfektes Duo (optional):</p>');
            recommendedVariants.forEach((variant) => {
              cards.push(
                buildCardMarkup({
                  title: variant.productTitle || 'Empfohlenes Produkt',
                  subtitle: variant.title ? `Nuance: ${variant.title}` : '',
                  image: variant.image || '',
                  role: 'recommended',
                  id: variant.id,
                  selected: selectedItemKeys.has(getSelectionKey('recommended', variant?.id)),
                })
              );
            });
          }

          recommendationsEl.hidden = false;
          recommendationsEl.innerHTML = cards.join('');
        };

        const showResult = (variants) => {
          if (!resultWrap) return;
          const { selectedShadeName } = getSelectionName();

          resultWrap.hidden = false;
          steps.forEach((el) => (el.hidden = true));
          if (errorEl) errorEl.hidden = true;
          if (progressContainer) progressContainer.hidden = true;
          if (matchBadge) matchBadge.textContent = '99.6% MATCH GEFUNDEN';
          if (resultKicker) resultKicker.textContent = 'Deine QUDE Nuance';
          if (resultName) resultName.textContent = selectedShadeName;
          matchingVariants = Array.isArray(variants) ? variants.slice() : [];
          selectedItemKeys = new Set(
            getSelectableItems()
              .map((item) => item.key)
              .filter(Boolean)
          );
          renderRecommendations();
          updateSelectButtonState();
        };

        const showNoMatch = () => {
          const { selectedShadeName } = getSelectionName();
          if (resultWrap) resultWrap.hidden = false;
          steps.forEach((el) => (el.hidden = true));
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = 'Fuer diese Auswahl haben wir aktuell keinen exakten Shade-Match gefunden.';
          }
          if (progressContainer) progressContainer.hidden = true;
          if (matchBadge) matchBadge.textContent = 'ANALYSE ABGESCHLOSSEN';
          if (resultKicker) resultKicker.textContent = 'Deine Auswahl';
          if (resultName) resultName.textContent = selectedShadeName;
          if (recommendationsEl) {
            recommendationsEl.innerHTML = '';
            recommendationsEl.hidden = true;
          }
          if (selectBtn) selectBtn.disabled = true;
        };

        const reset = () => {
          state.tone = null;
          state.undertone = null;
          matchingVariants = [];
          selectedItemKeys = new Set();
          isAddingShadeFinderItems = false;
          if (recommendationsEl) {
            recommendationsEl.innerHTML = '';
            recommendationsEl.hidden = false;
          }
          if (errorEl) errorEl.hidden = true;
          setSelectButtonLoading(false);
          showStep(1);
        };

        const recommend = () => {
          if (!activeConfig) return [];
          const candidates = activeConfig.variants.filter((v) => v && v.id && Array.isArray(v.options));
          if (!candidates.length) return [];

          const matchesValue = (variantValue, selected) =>
            Array.isArray(variantValue) ? variantValue.includes(selected) : variantValue === selected;

          const exact = candidates.filter((v) => {
            if (!v.tone || !v.undertone) return false;
            if (Array.isArray(v.tone) && !v.tone.length) return false;
            if (Array.isArray(v.undertone) && !v.undertone.length) return false;
            return matchesValue(v.tone, state.tone) && matchesValue(v.undertone, state.undertone);
          });

          if (!exact.length) return [];
          return exact.sort((a, b) => {
            if (!!a.available === !!b.available) return 0;
            return a.available ? -1 : 1;
          });
        };

        const addSelectedItemsToCart = async () => {
          const items = getSelectedCartItems();
          if (!items.length) return;

          const cart = getCart();
          const addUrl = getAddUrl();
          let lastResponseJson = null;

          isAddingShadeFinderItems = true;
          updateSelectButtonState();
          setSelectButtonLoading(true);
          if (errorEl) errorEl.hidden = true;

          try {
            for (const item of items) {
              const config = fetchConfig('javascript');
              config.headers = config.headers || {};
              config.headers['X-Requested-With'] = 'XMLHttpRequest';
              delete config.headers['Content-Type'];

              const formData = new FormData();
              formData.append('id', item.id);
              formData.append('quantity', item.quantity);

              if (cart && typeof cart.getSectionsToRender === 'function') {
                formData.append(
                  'sections',
                  cart.getSectionsToRender().map((section) => section.id)
                );
                formData.append('sections_url', window.location.pathname);
                if (typeof cart.setActiveElement === 'function') cart.setActiveElement(selectBtn);
              }

              config.body = formData;
              const response = await fetch(addUrl, config);
              lastResponseJson = await response.json();

              if (lastResponseJson?.status) {
                throw new Error(lastResponseJson.description || 'There was an error adding the selected items.');
              }
            }

            if (!lastResponseJson) throw new Error('There was an error adding the selected items.');

            if (cart && typeof cart.renderContents === 'function' && lastResponseJson.sections) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                await publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'shade-finder',
                  cartData: lastResponseJson,
                });
              }
              setOpen(false);
              cart.renderContents(lastResponseJson);
            } else if (document.querySelector('cart-drawer')) {
              const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble&t=${Date.now()}`);
              const sections = await response.json();
              setOpen(false);
              document.querySelector('cart-drawer')?.renderContents({
                sections,
                item_count: items.reduce((count, item) => count + Number(item.quantity || 0), 0),
              });
            } else if (window.routes?.cart_url) {
              setOpen(false);
              window.location = window.routes.cart_url;
            }
          } catch (e) {
            if (errorEl) {
              errorEl.hidden = false;
              errorEl.textContent = e?.message || 'There was an error adding the selected items.';
            }
          } finally {
            isAddingShadeFinderItems = false;
            setSelectButtonLoading(false);
            updateSelectButtonState();
          }
        };

        triggers.forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            reset();
            const key = trigger.getAttribute('data-shade-finder-source') || 'main';
            activeConfig = configs.get(key) || configs.get('main') || configs.values().next().value || null;
            setOpen(true);
          });
        });

        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            setOpen(false);
          });
        }

        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) setOpen(false);
        });

        overlay.querySelectorAll('.sf-option').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const key = btn.getAttribute('data-sf-key');
            const value = (btn.getAttribute('data-sf-value') || '').toLowerCase();
            if (!key || !value) return;
            state[key] = value;

            if (key === 'tone') {
              showStep(2);
              return;
            }
            if (key === 'undertone') {
              const exactMatches = recommend();
              if (exactMatches.length) showResult(exactMatches);
              else showNoMatch();
            }
          });
        });

        if (retryBtn) {
          retryBtn.addEventListener('click', (e) => {
            e.preventDefault();
            reset();
          });
        }

        if (selectBtn) {
          selectBtn.addEventListener('click', (e) => {
            e.preventDefault();
            if (isAddingShadeFinderItems) return;
            addSelectedItemsToCart();
          });
        }

        if (recommendationsEl) {
          recommendationsEl.addEventListener('change', (e) => {
            const toggle = e.target.closest('[data-sf-card-toggle]');
            if (!toggle) return;
            const card = toggle.closest('.product-recommendation');
            if (!card) return;
            const role = card.dataset.sfCardRole || '';
            const cardId = card.dataset.sfCardId || '';
            const nextSelected = !!toggle.checked;
            const selectionKey = getSelectionKey(role, cardId);

            if (nextSelected) selectedItemKeys.add(selectionKey);
            else selectedItemKeys.delete(selectionKey);

            card.dataset.selected = nextSelected ? 'true' : 'false';
            card.classList.toggle('is-selected', nextSelected);
            updateSelectButtonState();
          });
        }

        reset();
        setOpen(false);

        const hash = String(window.location.hash || '').toLowerCase();
        if (!window.__shadeFinderAutoOpened && hash && hash.includes('shadefinder')) {
          window.__shadeFinderAutoOpened = true;
          const key = 'main';
          activeConfig = configs.get(key) || configs.get('default') || configs.values().next().value || null;
          reset();
          setOpen(true);
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;

        try {
          const url = new URL(productUrl, window.location.origin);
          const variantId = url.searchParams.get('variant');
          if (variantId && this.variants.length > 0) {
            const variant = this.variants.find((v) => v.id == variantId);
            if (variant) {
              this.updateVariantInputs(variant.id);
              if (variant.featured_media) {
                const mediaGallery = this.querySelector(`media-gallery`) || this.querySelector(`media-gallery-new`);
                mediaGallery?.setActiveMedia?.(`${this.dataset.section}-${variant.featured_media.id}`, true);
              }
              this.productForm?.toggleSubmitButton(!variant.available, window.variantStrings.soldOut);
            }
          }
        } catch (e) {
          console.error('Error parsing product URL', e);
        }

        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          const mediaGallery = this.querySelector(`media-gallery`) || this.querySelector(`media-gallery-new`);
          if (mediaGallery?.applyVariantAltFilter) {
            const optionPosition = Number(mediaGallery.dataset.variantGalleryOptionPosition || 0);
            const filterValue =
              optionPosition > 0 && Array.isArray(variant?.options)
                ? variant.options[optionPosition - 1]
                : '';
            mediaGallery.applyVariantAltFilter(filterValue);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          if (variantId) {
            input.removeAttribute('disabled');
          } else {
            input.setAttribute('disabled', 'disabled');
          }
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const mediaGallerySource = this.querySelector('media-gallery ul, media-gallery-new ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul, media-gallery-new ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM (append to keep existing order)
          for (let i = 0; i < mediaGalleryDestinationItems.length; i++) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.append(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet] = refreshSourceData();
        }

        // set featured media as active in the media gallery
        const mediaGallery = this.querySelector(`media-gallery`) || this.querySelector(`media-gallery-new`);
        if (variantFeaturedMediaId) {
          mediaGallery?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
